/**
 * Route-kind branching for the global breadcrumb, kept out of the hook.
 *
 * The hook's job is to read state; deciding which reading wins is a pure
 * decision, and keeping it here means the interesting cases (a thread whose
 * project has loaded but whose title has not, a stale published label) are
 * unit-testable without mounting a router.
 */

import {
  splitPathname,
  withParentCrumb,
  withResolvedLabel,
  withResolvedLeafLabel,
  type BreadcrumbCrumb,
  type BreadcrumbCrumbIcon,
} from "./globalBreadcrumb.logic";
import type { PublishedBreadcrumbLabel } from "./globalBreadcrumbStore";

/** Plain ids, so this module stays free of branded contract types. */
export interface BreadcrumbProjectRef {
  readonly environmentId: string;
  readonly projectId: string;
}

export interface BreadcrumbRouteInput {
  readonly pathname: string;
  readonly staticCrumbs: readonly BreadcrumbCrumb[];
  /** A crumb label the current page published for itself, if any. */
  readonly publishedLabel: PublishedBreadcrumbLabel | null;
  readonly cmmdProjectTitle: string | null;
  /** Project artwork for the CMMD project route's leaf crumb. */
  readonly cmmdProjectIcon: BreadcrumbCrumbIcon | null;
  /**
   * Project artwork for a Forge project reached through a runtime — the
   * project ancestor on a thread route. Null
   * until the identity is known: the bar renders the name alone rather than
   * waiting, because it is mounted above every route.
   */
  readonly projectIcon: BreadcrumbCrumbIcon | null;
  readonly projectName: string | null;
  readonly projectRouteKey?: string | null;
  readonly projectRef: BreadcrumbProjectRef | null;
  readonly threadTitle: string | null;
  readonly isThreadRoute: boolean;
}

/**
 * Where the project shown on a thread or draft route comes from.
 *
 * A saved thread carries its own project; a draft carries it on the composer
 * session. Collapsing both here
 * is what lets the project crumb appear on the new-thread flow rather than only
 * on threads that have already been saved.
 */
export function resolveBreadcrumbProjectRef(input: {
  readonly pathname: string;
  readonly threadProjectRef: BreadcrumbProjectRef | null;
  readonly draftProjectRef: BreadcrumbProjectRef | null;
}): BreadcrumbProjectRef | null {
  if (input.threadProjectRef !== null) {
    return input.threadProjectRef;
  }
  if (input.draftProjectRef !== null) {
    return input.draftProjectRef;
  }

  // There used to be a third source here: `/project/:env/:id` carried the ref
  // in its own URL. That route is gone, so a thread and a draft are the only
  // things that can name a project now.
  return null;
}

/**
 * Picks the best crumbs available for the current route.
 *
 * A page's published label is applied last rather than short-circuiting, so it
 * composes with the derived crumbs instead of replacing them — a thread can
 * publish its title and still keep the project parent resolved from the store.
 */
export function resolveBreadcrumbCrumbs(input: BreadcrumbRouteInput): readonly BreadcrumbCrumb[] {
  const derived = resolveDerivedCrumbs(input);
  return input.publishedLabel === null
    ? derived
    : withResolvedLabel(derived, input.publishedLabel.key, input.publishedLabel.label);
}

function resolveDerivedCrumbs(input: BreadcrumbRouteInput): readonly BreadcrumbCrumb[] {
  const segments = splitPathname(input.pathname);
  const first = segments[0];

  if (first === "cmmd" || first === "project") {
    return withResolvedLabel(
      input.staticCrumbs,
      "project-detail",
      input.cmmdProjectTitle ?? "Project",
    ).map((crumb) =>
      crumb.key === "project-detail" && input.cmmdProjectIcon !== null
        ? Object.assign({}, crumb, { icon: input.cmmdProjectIcon })
        : crumb,
    );
  }

  if (input.isThreadRoute) {
    return resolveThreadCrumbs(input);
  }

  return input.staticCrumbs;
}

function resolveThreadCrumbs(input: BreadcrumbRouteInput): readonly BreadcrumbCrumb[] {
  const leafResolved = withResolvedLeafLabel(input.staticCrumbs, input.threadTitle);
  if (input.projectName === null || input.projectRef === null) {
    return leafResolved;
  }

  return withParentCrumb(leafResolved, {
    key: "thread-project",
    to: `/project/${encodeURIComponent(input.projectRouteKey ?? input.projectRef.projectId)}`,
    label: input.projectName,
    // The thread's own leaf keeps no mark: it is a thread, not a project.
    ...(input.projectIcon === null ? {} : { icon: input.projectIcon }),
  });
}
